import * as React from "react";
import {
  useNavigate,
  type NavigateFunction,
  type NavigateOptions,
  type To,
} from "react-router-dom";

import { Goto } from "@/shared/navigation";
import { useCollaborationBar } from "@/shared/state/collaborationBar";

interface SidebarHandoff {
  /**
   * Replaces the app's {@link Goto} for the conversation subtree: every
   * navigation through it moves the thread into the chat sidebar first.
   */
  goto: Goto;

  /**
   * True once the sidebar owns this thread, and the page's copy of the
   * conversation must therefore stop executing client tools.
   */
  handedOff: boolean;
}

/**
 * Hands a thread to the chat sidebar whenever something inside the
 * conversation navigates the page away from it.
 *
 * The thread detail page mounts its own copy of the conversation, so a
 * navigating tool (`client_navigate`, `client_visualize_open_file`) unmounts
 * the client-tool executor mid-batch: the approval prompt for a later tool in
 * the batch goes with it, and `isOnVisualizerPage()` freezes on the
 * pre-navigation route, so a follow-up `client_visualize_seek` reports the
 * user is off the visualizer while the browser sits on it. The sidebar lives
 * outside the router outlet, so handing the thread over keeps one live
 * executor across the navigation.
 *
 * Wrapping {@link Goto} rather than the navigate tool also covers in-message
 * entity links, which route through the same context and lose the
 * conversation the same way. Navigation started outside the conversation (the
 * breadcrumb, the nav rail) is a deliberate exit and stays unwrapped.
 */
export function useSidebarHandoffGoto(threadId: string): SidebarHandoff {
  const navigate = useNavigate();
  const { open: openCollaborationBar } = useCollaborationBar();
  const [handedOff, setHandedOff] = React.useState(false);

  const goto = React.useMemo(() => {
    // `NavigateFunction` overloads a path form and a history-delta form, and
    // one plain function cannot satisfy both. `Goto` always navigates by
    // path, so the delta overload is unreachable.
    const handOffThenNavigate = ((to: To, options?: NavigateOptions) => {
      openCollaborationBar({
        mode: "ai-agent-chat",
        threadId,
        view: "thread",
      });
      setHandedOff(true);
      navigate(to, options);
    }) as NavigateFunction;

    return new Goto(handOffThenNavigate);
  }, [navigate, openCollaborationBar, threadId]);

  return { goto, handedOff };
}
